import React , { useEffect , useState , useMemo } from 'react'
import { Box, CircularProgress, Divider, Skeleton, Typography } from '@mui/material';
import { makeStyles, useTheme } from '@mui/styles'
import dayjs from 'dayjs';

import { useServices, useSnackbar } from 'contexts';
import { generate4CFeedback, getRecentCodeChallengeAttempts } from 'services';
import { AttemptStates } from './constant';
import { getCompanyImageProps } from 'utils/imageHelper';
import { WhiteBox, WhiteBoxHeader } from 'components/AppShell';
import { NoResultComponent } from 'components/Home/TodoList';
import { capitalizeFirstLetter, capitalizeFirstLetterOfNWords, Session } from 'utils';
import { useNavigateWithClient } from 'hooks';
import CustomButton from 'components/CustomButton';
import StartAssessmentDialog from 'dialogs/StartAssessmentDialog';
import NoAccessTooltip from 'components/NoAccessTooltip';
import { useSelectedBatchRemoved } from 'announcementStore';

const useStyles = makeStyles((theme) => ({
  box : {
    display : 'flex',
    flexDirection : 'column',
    width : '100%',
    gap : theme.spacing(5),
    marginTop : theme.spacing(5),
    maxHeight : '405px',
    overflow : 'auto',
    height : '405px',
    paddingRight : theme.spacing(3),
    overflowX: 'hidden'
  },
  cardContainer: {
    display: 'flex',
    borderRadius: theme.spacing(2),
    width : '100%',
  },
  category : {
    width : '82px',
    height : '82px',
    backgroundColor : theme.palette.neutral['clr-50'],
    borderRadius: theme.spacing(2,0,0,2),
    display : 'flex',
    justifyContent : 'center',
    alignItems : 'center'
  },
  content : {
    padding : theme.spacing(3,6),
    display : 'flex',
    justifyContent : 'space-between',
    width : '100%'
  },
  topicDetails : {
    display : 'flex',
    flexDirection : 'column',
    gap : theme.spacing(2)
  },
  bottomContainer : {
    padding: theme.spacing(2), 
    display:"flex",
    justifyContent:"space-between",
    alignItems : 'center',
    marginTop : theme.spacing(2),
    background: `linear-gradient(360deg, #FFFFFF 80%, rgba(255, 255, 255, 0) 100%)`,
    position : 'relative',
    bottom : '0px',
  }
}))

export const AttemptCard = ({ 
  attempt, 
  setRefresh,
  generatingFeedback,
  setGeneratingFeedback,
  currentGeneratingId,
  setCurrentGeneratingId,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const service = useServices();
  const snackbar = useSnackbar();
  const navigateWithClient = useNavigateWithClient();

  const removed = useSelectedBatchRemoved();

  const [attemptState, setAttemptState] = useState(attempt.status);
  const [attemptTags , setAttemptTags] = useState([])
  const [startInterview , setStartInterview] = React.useState(false);

  const generateFeedback = async () => {
    try {
      setGeneratingFeedback(true);
      setCurrentGeneratingId(attempt._id);
      await service.callService(generate4CFeedback, attempt._id);
    } catch (err) {
      console.error(err.message);
      snackbar.error('Failed to generate feedback')
    } finally {
      snackbar.success('Feedback generated')
      setGeneratingFeedback(false);
      setCurrentGeneratingId(null);
      setRefresh(true);
    }
  };

  const { companyTag, categoryName, challenge } = useMemo(() => {
    const tags = attempt?.codeChallenges?.[0]?.tags || [];
    setAttemptTags(attempt?.codeChallenges?.[0]?.tags);
    const companyTag = tags.find(tag => tag.category === 'company');
    const subTopicTag = tags.find(tag => tag.category === 'subTopic');

    const categoryName = capitalizeFirstLetter(
      companyTag?.name || subTopicTag?.name || 'Unknown'
    );

    return {
      companyTag,
      categoryName,
      challenge: attempt?.codeChallenges?.[0],
    };
  }, [attempt]);

  const colors = useMemo(() => {
    const borderColors = {
      [AttemptStates.FEEDBACK_SKIPPED]: theme.palette.danger['clr-500'],
      [AttemptStates.FEEDBACK_PENDING]: theme.palette.warning['clr-500'],
    };

    const baseColor = borderColors[attemptState] || theme.palette.neutral['clr-200'];
    const hoverColor = borderColors[attemptState] || theme.palette.primary['clr-100'];

    return { baseColor, hoverColor };
  }, [attemptState, theme]);

  const getFeedbackMessage = () => {
    switch (attemptState) {
      case AttemptStates.FEEDBACK_SKIPPED:
        return 'Feedback isn’t available for this attempt as no code was submitted';
      case AttemptStates.FEEDBACK_PENDING:
        return 'Feedback generation is pending!';
      default:
        return null;
    }
  };

  const FeedbackNote = () => {
    const message = getFeedbackMessage();
    if (!message) return null;

    const color =
      attemptState === AttemptStates.FEEDBACK_SKIPPED
        ? theme.palette.danger['clr-700']
        : theme.palette.warning['clr-700'];

    return (
      <Typography variant='body01-bold' color={color} component='span'>
        &nbsp;({message})
      </Typography>
    );
  };

  const RenderButtons = () => {
    switch (attemptState) {
      case AttemptStates.FEEDBACK_PENDING:
        const isThisGenerating = generatingFeedback && currentGeneratingId === attempt._id;
        return (
          <CustomButton 
            onClick={generateFeedback}
            disabled={generatingFeedback}
            startIcon={isThisGenerating ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isThisGenerating ? 'Generating...' : 'Generate feedback'}
          </CustomButton>
        );
      case AttemptStates.FEEDBACK_GENERATED:
        return (
          <>
            {
              attempt?.assessmentEntryPoint?.data !== 'contest' &&
                <NoAccessTooltip show={removed}>
                  <CustomButton onClick={() => setStartInterview(true)} disabled={removed}>Reattempt</CustomButton>
                </NoAccessTooltip>
            }
            <CustomButton
              onClick={() => navigateWithClient(`/performance/4c/${attempt._id}`)}
              variant='outlined'
            >
              View feedback
            </CustomButton>
          </>
        );
      default:
        return null;
    }
  };

  const CompanyImage = getCompanyImageProps(attemptTags, attempt?.assessmentEntryPoint?.tagId , {
    fallback: attempt?.assessmentEntryPoint?.data === "personalized"
        ? "https://assets.languify.in/images/personalizedTag.png" 
        : 'https://assets.languify.in/images/codeTag.png',
    width: 54,
    style: {
      backgroundColor: theme.palette.shades['clr-white-900'],
      borderRadius: theme.spacing(3),
      width: 56,
      height : 56,
      display : 'flex',
      justifyContent : 'center',
      alignItems : 'center',
      border : '1px solid #CCD4DE'
    },
    alt: 'Company tag',
  });

  return (
    <Box
      className={classes.cardContainer}
      sx={{
        border: `2px solid ${colors.baseColor}`,
        '&:hover': {
          border: `2px solid ${colors.hoverColor}`,
        },
      }}
    >
      <Box className={classes.category}>
        <CompanyImage />
      </Box>

      <Box className={classes.content}>
        <Box className={classes.topicDetails}
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          <Box display='flex' alignItems='center' gap='8px'>
            
            <Typography
              variant='h5-medium'
              color='neutral.clr-600'
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
                minWidth: 0,
                display: 'block',
              }}
            >
              <span style={{ color: '#111519' }}>
                {categoryName}: {capitalizeFirstLetterOfNWords(challenge?.difficulty)} -
              </span>&nbsp;
              { challenge?.title }
            </Typography>
          </Box>

          <Typography variant='body01-bold' color='neutral.clr-600'>
            Attempted on: {dayjs(attempt?.startedAt).format('DD/MM/YYYY')}
            <FeedbackNote />
          </Typography>
        </Box>

        <Box display='flex' gap='12px' alignItems='center'>
          <RenderButtons />
        </Box>
      </Box>
      <StartAssessmentDialog
          onClose={() => setStartInterview(false)}
          open={startInterview}
          template={{ _id : attempt.codeChallenges[0]._id , name : attempt.codeChallenges[0].title}}
          codingInterview={true}
          stateParams={attempt.assessmentEntryPoint}
        />
    </Box>
  );
};

const RecentAttempts = ({ onAttemptStatusCheck }) => {
  const service = useServices();
  const classes = useStyles();

  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh , setRefresh] = useState(false)
  const [generatingFeedback, setGeneratingFeedback] = useState(false); 
  const [currentGeneratingId, setCurrentGeneratingId] = useState(null); 

  const [page, setPage] = useState(0); 
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    const fetchRecentAttempts = async () => {
      try {
        setLoading(true);
        setRefresh(false)
        const result = await service.callService(getRecentCodeChallengeAttempts, {
          include: "codeChallenges",
          user : Session.userId,
          "assessmentEntryPoint.data": ["personalized", "general_dsa", "company", "contest"]
        });

        setAttempts(result);
        setTotalCount(result.total);
        checkFirstFiveAttempts(result);
      } catch (err) {
        console.error(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentAttempts();
  }, [page , refresh]); 

  const checkFirstFiveAttempts = (attemptList) => {
    let hasPending = false;
    let hasSkipped = false;

    for (let i = 0; i < Math.min(5, attemptList.length); i++) {
      const attempt = attemptList[i];
      if (attempt.status === AttemptStates.FEEDBACK_PENDING) {
        hasPending = true;
      }
      if (attempt.status === AttemptStates.FEEDBACK_SKIPPED) {
        hasSkipped = true;
      }
    }

    if (onAttemptStatusCheck) {
      onAttemptStatusCheck({ hasPending, hasSkipped }); 
    }
  };

  return (
    <WhiteBox>
      <WhiteBoxHeader
        id="recent-attempts"
        heading="Recent attempts"
        subheading="History of all your challenge attempts"
      />
      <Divider />
      <Box className={classes.box}>
        {
          loading 
          ? Array.from({ length: 3 }).map((_, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                border: '2px solid #E3EAF3',
                borderRadius: 2,
                width: '100%',
                mb: 3,
              }}
            >
              <Box
                sx={{
                  width: '82px',
                  height: '82px',
                  backgroundColor: '#F8F9FB',
                  borderRadius: '16px 0 0 16px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mt: 3,
                  ml: 3,
                }}
              >
                <Skeleton variant="circular" width={56} height={56} />
              </Box>
        
              <Box
                sx={{
                  flex: 1,
                  padding: 3,
                  pl: 6,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ flex: 1, overflow: 'hidden', minWidth: 0 }}>
                  <Skeleton width="70%" height={24} sx={{ mb: 1 }} />
                  <Skeleton width="40%" height={20} />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Skeleton variant="rectangular" width={120} height={36} />
                  <Skeleton variant="rectangular" width={120} height={36} />
                </Box>
              </Box>
            </Box>
          ))        
          :
            attempts?.length ? (
              attempts.map((attempt, index) => (
                <AttemptCard 
                  key={index} 
                  attempt={attempt} 
                  setRefresh={setRefresh}
                  generatingFeedback={generatingFeedback}
                  setGeneratingFeedback={setGeneratingFeedback}
                  currentGeneratingId={currentGeneratingId}
                  setCurrentGeneratingId={setCurrentGeneratingId}
                />
              ))
            ) : (
              <NoResultComponent
                description={
                  <>
                    Nothing to show here! <br />
                    Attempt any challenge inside company or DSA topics.
                  </>
                }
                image='https://assets.languify.in/images/noPreparation.png'
              />
            )
        }
      </Box>
      {/* <Box className={classes.bottomContainer}>
        <Typography variant='body01-bold' color='neutral.clr-900'>
          {totalCount > 0 
            ? `Attempts: ${page * pageSize + 1}-${Math.min((page + 1) * pageSize, totalCount)} of ${totalCount}` 
            : 'No attempts'
          }
        </Typography>
        <Pagination
          count={Math.max(1, Math.ceil(totalCount / pageSize))}
          page={page}
          setPage={setPage}
        />
      </Box> */}
    </WhiteBox>
  );
};


export default RecentAttempts